// App-side feature-flag consumption. Fetches the resolved flag map for the
// current tenant once (auth-service /feature-flags/me via the gateway) and gates
// UI/behaviour on it. Management + rollout live in the admin-portal; here we only
// READ the resolved values. Flags default OFF until loaded / when unknown, so a
// gated feature never flickers on before its state is known.
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { gatewayApi } from '@/lib/hrApi';
import { useAuth } from '@/app/hooks';

export type FeatureFlagMap = Record<string, boolean>;

async function fetchFlags(): Promise<FeatureFlagMap> {
  const { data } = await gatewayApi.get('/api/v1/feature-flags/me');
  return (data as FeatureFlagMap) || {};
}

export function useFeatureFlags() {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: ['feature-flags', 'me'],
    queryFn: fetchFlags,
    enabled: !!isAuthenticated,
    staleTime: 5 * 60_000, // flags change rarely; avoid refetch churn
    retry: 1, // non-critical UI gating — don't hammer on failure
  });
}

/**
 * Whether a feature flag is ON for the current tenant. Defaults to `fallback`
 * (false) until the map is loaded or if the key is unknown, so a gated feature
 * stays hidden rather than flashing.
 */
export function useFeatureFlag(key: string, fallback = false): boolean {
  const { data } = useFeatureFlags();
  if (!data || !(key in data)) return fallback;
  return data[key];
}

/** Declarative guard: renders children only when the flag is ON. */
export const FeatureGate: React.FC<{
  flag: string;
  fallback?: React.ReactNode;
  children: React.ReactNode;
}> = ({ flag, fallback = null, children }) => {
  const on = useFeatureFlag(flag);
  return <>{on ? children : fallback}</>;
};
